/**
 * `TITLE.TTM`, tick by tick, in the TTM interpreter's own command vocabulary.
 * Transcribed from the verified disassembly in
 * `docs/game/ui-reference/intro.md` (39 frames, `VOLUME.003`, v3.38) and
 * checked against the live interpreter by `shell-intro-transcript.test.ts`,
 * which parses the real `TITLE.TTM` and asserts this list matches command for
 * command.
 *
 * This exists purely as the data path: the bake pipeline has no `.TTM` stage
 * yet, so there is nothing under `/baked` for the interpreter to run. Delete
 * this file the day `data/baked/scripts/TITLE.TTM.json` exists.
 */

import { FULL_SCREEN_CLIP, MS_PER_FRAME, type TtmCommand, type TtmSpriteFlip } from "@red-opus/formats";
import type { IntroTick } from "./intro-ticks.ts";

function delayCommand(frames: number): TtmCommand {
  return { kind: "delay", frames, ms: frames * MS_PER_FRAME };
}

function sprite(sheet: number, id: string, frame: number, x: number, y: number, flip?: TtmSpriteFlip): TtmCommand {
  return { kind: "drawSprite", sheet, id, frame, x, y, flip, dstSize: undefined, size: undefined, clip: FULL_SCREEN_CLIP };
}

function fade(kind: "fadeIn" | "fadeOut", speed: number): TtmCommand {
  return { kind, colorStart: 0, colorCount: 256, targetColor: 0, speed };
}

/** `DYNAMIX.BMP`'s eight pre-rendered sizes and where each is stamped — the logo "zoom" is discrete frames, not scaling. */
const PULSE: readonly (readonly [number, number, number])[] = [
  [0, 237, 65],
  [1, 236, 63],
  [2, 233, 61],
  [3, 229, 57],
  [4, 225, 52],
  [5, 219, 46],
  [6, 210, 38],
  [7, 198, 25],
];

/** `TITLE.BMP` frame, position, flip and the `STORE AREA` that follows each bullet-hole stamp. */
const BULLET_HOLES: readonly (readonly [number, number, number, TtmSpriteFlip | undefined, number, number, number, number])[] = [
  [0, 139, 11, undefined, 136, 10, 24, 13],
  [1, 173, 31, undefined, 168, 29, 24, 18],
  [2, 159, 62, undefined, 152, 60, 24, 15],
  [0, 191, 75, "h", 192, 73, 16, 17],
  [1, 177, 112, "h", 176, 110, 24, 21],
  [2, 212, 132, "h", 208, 129, 24, 19],
  [2, 199, 159, undefined, 192, 158, 24, 13],
  [3, 233, 177, undefined, 232, 174, 24, 18],
];

function preloadTick(): IntroTick {
  return {
    index: 0,
    delayMs: 0,
    commands: [
      { kind: "loadSong", slot: 0, id: "DYNAMIX.SNG" },
      { kind: "loadSong", slot: 1, id: "TITLE.SNG" },
      { kind: "setPalette", slot: 0 },
      { kind: "loadPalette", slot: 0, id: "DYNAMIX.PAL" },
      { kind: "loadSpriteSheet", slot: 0, id: "DYNAMIX.BMP" },
      { kind: "setPalette", slot: 1 },
      { kind: "loadPalette", slot: 1, id: "TITLE.PAL" },
      { kind: "loadSpriteSheet", slot: 1, id: "TITLE.BMP" },
      { kind: "selectSoundSlot", slot: 0 },
      { kind: "loadSoundBank", slot: 0, id: "SIMSNDS.SNG" },
      { kind: "selectSoundSlot", slot: 1 },
      { kind: "loadSoundBank", slot: 1, id: "SHLSNDS2.SNG" },
    ],
  };
}

function pulseTicks(): IntroTick[] {
  const order = [...PULSE, PULSE[7], ...[...PULSE].reverse().slice(1)] as readonly (readonly [number, number, number])[];
  return order.map((step, offset) => {
    const [frame, x, y] = step ?? [0, 237, 65];
    const commands: TtmCommand[] = [sprite(0, "DYNAMIX.BMP", frame, x, y)];
    if (offset === 0) commands.push(delayCommand(3), { kind: "selectSoundSlot", slot: 0 }, { kind: "playSound", id: 0 });
    return { index: 2 + offset, commands, delayMs: 3 * MS_PER_FRAME };
  });
}

function bulletHoleTicks(): IntroTick[] {
  return BULLET_HOLES.map(([frame, x, y, flip, sx, sy, sw, sh], offset) => {
    const commands: TtmCommand[] = [
      sprite(1, "TITLE.BMP", frame, x, y, flip),
      { kind: "storeArea", x: sx, y: sy, w: sw, h: sh },
    ];
    if (offset === 0) commands.push(delayCommand(2));
    return { index: 27 + offset, commands, delayMs: 2 * MS_PER_FRAME };
  });
}

export const TITLE_TTM_TICKS: readonly IntroTick[] = [
  preloadTick(),
  {
    index: 1,
    delayMs: 120 * MS_PER_FRAME,
    commands: [
      { kind: "setPalette", slot: 0 },
      { kind: "loadScreen", id: "DYNAMIX.SCR", size: undefined },
      fade("fadeIn", 2),
      delayCommand(120),
    ],
  },
  ...pulseTicks(),
  { index: 18, delayMs: 50 * MS_PER_FRAME, commands: [delayCommand(50), { kind: "stopMusic" }] },
  { index: 19, delayMs: 100 * MS_PER_FRAME, commands: [delayCommand(100), { kind: "stopSound", id: 0 }] },
  { index: 20, delayMs: 100 * MS_PER_FRAME, commands: [fade("fadeOut", 3)] },
  { index: 21, delayMs: 0, commands: [delayCommand(0)] },
  { index: 22, delayMs: 30 * MS_PER_FRAME, commands: [{ kind: "setPalette", slot: 1 }, delayCommand(30)] },
  {
    index: 23,
    delayMs: 30 * MS_PER_FRAME,
    commands: [{ kind: "loadScreen", id: "TITLE.SCR", size: undefined }, fade("fadeIn", 2)],
  },
  { index: 24, delayMs: 240 * MS_PER_FRAME, commands: [delayCommand(240), { kind: "playSound", id: 25 }] },
  { index: 25, delayMs: 40 * MS_PER_FRAME, commands: [delayCommand(40)] },
  { index: 26, delayMs: 40 * MS_PER_FRAME, commands: [{ kind: "selectSoundSlot", slot: 1 }, { kind: "playSound", id: 0 }] },
  ...bulletHoleTicks(),
  { index: 35, delayMs: 5 * MS_PER_FRAME, commands: [delayCommand(5)] },
  {
    index: 36,
    delayMs: 330 * MS_PER_FRAME,
    commands: [
      delayCommand(330),
      { kind: "stopSound", id: 0 },
      { kind: "selectSoundSlot", slot: 0 },
      { kind: "stopSound", id: 25 },
    ],
  },
  { index: 37, delayMs: 0, commands: [fade("fadeOut", 2), delayCommand(0)] },
  {
    index: 38,
    delayMs: 0,
    commands: [{ kind: "unknown", op: 0x2010, name: "SET FRAME", args: { form: "ints", values: [1, 0] } }],
  },
];
